import React from "react";
import { format } from "date-fns";
import { FormValues } from "components/medications/create-medication/duck";
import { casesActions } from "configs";
import {
  generateId,
  UpdateCardProps,
  useCurrentCase,
  useDispatch,
  useParams,
  convertDoseToMg,
  useInitializeOfflineData
} from "duck";
import {
  MedicationCalculationTypeEnum,
  RouteEnum,
  useCreateMedicationCalculationsMutation,
  useDeleteMedicationCalculationsMutation,
  useUpdateMedicationCalculationsMutation
} from "requests";

export const useCreateMedication = () => {
  const { id: caseId } = useParams();
  const dispatch = useDispatch();
  const [create] = useCreateMedicationCalculationsMutation();
  const { patientId, shouldSync } = useCurrentCase(medicalCase => ({
    shouldSync: medicalCase.shouldSync,
    patientId: medicalCase.data.client.patient._id
  }));
  const { loadMedications } = useInitializeOfflineData();

  return (
    values: FormValues,
    calculationType: MedicationCalculationTypeEnum
  ) => {
    const isNonEmergencyMedication =
      calculationType === MedicationCalculationTypeEnum.Additional ||
      calculationType === MedicationCalculationTypeEnum.Induction ||
      calculationType === MedicationCalculationTypeEnum.PreAnesthetic;

    const variables = {
      calculationType,
      medication: values.name,
      concentration: values.concentration,
      dose: convertDoseToMg(values.dose, values.doseUnit),
      route: values.route as RouteEnum,
      volumeAdministered: values.volumeAdministered,
      additionalVolumeAdministered: values.additionalVolumeDrawn,
      additionalVolumeDrawn: values.additionalVolumeAdministered,
      ...(isNonEmergencyMedication && {
        initials: values.initials
      })
    };

    if (shouldSync) {
      return create({
        variables: {
          input: {
            patient: patientId,
            timeAdministered: values.timeAdministered,
            ...variables
          }
        }
      }).then(response => {
        loadMedications();
        const newMedication =
          response.data?.createMedicationCalculation?.medicationCalculation;
        if (newMedication) {
          dispatch(
            casesActions.addMedicationCalculation({
              _id: caseId,
              data: newMedication
            })
          );
        }
      });
    }

    dispatch(
      casesActions.addMedicationCalculation({
        _id: caseId,
        data: {
          fakeId: generateId(),
          timeAdministered: values.timeAdministered
            ? format(values.timeAdministered, "k:mm:ssxxx")
            : null,
          ...variables
        }
      })
    );

    return Promise.resolve();
  };
};

export const useUpdateMedication = () => {
  const [update] = useUpdateMedicationCalculationsMutation({
    ignoreResults: true
  });
  const { shouldSync, caseId } = useCurrentCase(medicalCase => ({
    shouldSync: medicalCase.shouldSync,
    caseId: medicalCase.data._id
  }));

  const dispatch = useDispatch();

  return React.useCallback(
    (props: UpdateCardProps) => {
      const { idOrFakeId, ...data } = props;

      if (shouldSync) {
        dispatch(
          casesActions.updateMedicationCalculation({
            data: {
              ...(typeof idOrFakeId === "number"
                ? { _id: idOrFakeId }
                : { fakeId: idOrFakeId }),
              ...data
            },
            _id: caseId
          })
        );

        if (typeof idOrFakeId === "number") {
          return update({ variables: { input: { _id: idOrFakeId, ...data } } });
        }
      }

      dispatch(
        casesActions.updateMedicationCalculation({
          _id: caseId,
          data: {
            ...(typeof idOrFakeId === "number"
              ? { _id: idOrFakeId }
              : { fakeId: idOrFakeId }),
            ...data
          }
        })
      );
    },
    [update, shouldSync]
  );
};

export const useDeleteMedication = () => {
  const dispatch = useDispatch();
  const [deleteMedication] = useDeleteMedicationCalculationsMutation({
    ignoreResults: true
  });
  const { shouldSync, caseId } = useCurrentCase(medicalCase => ({
    shouldSync: medicalCase.shouldSync,
    caseId: medicalCase.data._id
  }));

  return (ids: (number | string)[]) => {
    if (shouldSync) {
      const filteredIds = ids.filter(id => !Number.isNaN(id));
      return deleteMedication({
        variables: { input: { ids: filteredIds as number[] } }
      }).then(() => {
        dispatch(
          casesActions.deleteMedicationCalculations({ data: ids, _id: caseId })
        );
      });
    }

    dispatch(
      casesActions.deleteMedicationCalculations({ data: ids, _id: caseId })
    );
  };
};
